import { useState } from "react";
import { cn } from "@/lib/utils";

/** Fixed height: fits four list rows (p-4 + space-y-2 + py-2 text-sm rows). */
const SKILL_FLIP_CARD_HEIGHT_CLASS = "h-[204px]";

/**
 * @param {{ group: { id: string; title: string; items: string[] } }} props
 */
export function SkillFlipCard({ group }) {
  const [flipped, setFlipped] = useState(false);

  const toggle = () => setFlipped((f) => !f);

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div className="[perspective:1000px]">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={flipped}
        aria-label={`${group.title}. ${flipped ? "Skills list visible" : "Category title"}. Press Enter or Space to flip.`}
        data-testid={`skills-card-${group.id}`}
        onClick={toggle}
        onKeyDown={onKeyDown}
        className={cn(
          /* overflow-hidden here breaks preserve-3d / backface-visibility in descendants */
          "relative cursor-pointer rounded-xl border border-gray-200 bg-white shadow-sm outline-none transition-shadow duration-300 hover:shadow-md",
          SKILL_FLIP_CARD_HEIGHT_CLASS,
          "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        )}
      >
        <div
          className={cn(
            "relative rounded-xl [transform-style:preserve-3d] transition-transform duration-500 ease-in-out",
            SKILL_FLIP_CARD_HEIGHT_CLASS,
            flipped ? "[transform:rotateY(180deg)]" : "[transform:rotateY(0deg)]"
          )}
        >
          <div
            className={cn(
              "absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white p-6",
              "backface-hidden [-webkit-backface-visibility:hidden]",
              "[transform:translateZ(1px)]"
            )}
          >
            <h3
              className="font-heading text-center text-xl font-semibold text-gray-900"
              data-testid={`skills-title-${group.id}`}
            >
              {group.title}
            </h3>
            <p className="mt-4 text-center text-xs text-gray-500">Click to flip</p>
          </div>

          {/* Transform on outer only: overflow on a transformed element often breaks scrolling. */}
          <div
            className={cn(
              "absolute inset-0 rounded-xl bg-white",
              "backface-hidden [-webkit-backface-visibility:hidden]",
              "[transform:translateZ(1px)_rotateY(180deg)]"
            )}
          >
            <div
              className="h-full min-h-0 overflow-hidden p-4"
              data-testid={`skills-list-scroll-${group.id}`}
            >
              <ul className="space-y-2" data-testid={`skills-list-${group.id}`}>
                {group.items.map((item, index) => (
                  <li
                    key={`${group.id}-${item}`}
                    className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
                    data-testid={`skills-item-${group.id}-${index}`}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
